import type { Action } from 'state/actions';
import type {
  BasketOptimizerCouponFromChanged,
  BasketOptimizerCouponToChanged,
  BasketOptimizerIsMutuallyExclusiveToggled,
  BasketOptimizerOptimizeCanceled,
  BasketOptimizerOptimizeIdReceived,
  BasketOptimizerOptimizeRequested,
  BasketOptimizerOptimizeRequestFailed,
  BasketOptimizerResultReceived,
  BasketOptimizerTargetBasketSizeChanged,
  BasketOptimizerUnderlyingTagsChanged,
} from 'state/advisory/basket-optimizer/actions';
import {
  BasketOptimizer,
  BasketOptimizerState,
  initialBasketOptimizerState,
} from 'state/advisory/basket-optimizer/model';
import { addOrReplaceKey, removeKey } from 'state/utils/collection';
import { now } from 'utils/core/now';

export function basketOptimizerReducer(state: BasketOptimizerState = {}, action: Action): BasketOptimizerState {
  switch (action.type) {
    case 'BASKETOPTIMIZER_IS_MUTUALLY_EXCLUSIVE_TOGGLED':
      return basketOptimizerIsMutuallyExclusiveToggled(state, action);
    case 'BASKETOPTIMIZER_TARGET_BASKET_SIZE_CHANGED':
      return basketOptimizerTargetBasketSizeChanged(state, action);
    case 'BASKETOPTIMIZER_COUPON_FROM_CHANGED':
      return basketOptimizerCouponFromChanged(state, action);
    case 'BASKETOPTIMIZER_COUPON_TO_CHANGED':
      return basketOptimizerCouponToChanged(state, action);
    case 'BASKETOPTIMIZER_OPTIMIZE_REQUESTED':
      return basketOptimizerOptimizeRequested(state, action);
    case 'BASKETOPTIMIZER_OPTIMIZE_CANCELED':
      return onQuoteWorkflowQuoteCancel(state, action);
    case 'BASKETOPTIMIZER_OPTIMIZE_ID_RECEIVED':
      return basketOptimizerOptimizeIdReceived(state, action);
    case 'BASKETOPTIMIZER_RESULT_RECEIVED':
      return basketOptimizerResultReceived(state, action);
    case 'BASKETOPTIMIZER_OPTIMIZE_REQUEST_FAILED':
      return basketOptimizerOptimizeRequestFailed(state, action);

    case 'BASKET_OPTIMIZER_UNDERLYING_TAGS_CHANGED':
      return onBasketOptimizerUnderlyingTagsChanged(state, action);

    case 'QUOTE_REMOVED':
      if (action.workspace !== 'static') {
        return state;
      }
      return removeKey(state, action.tabId);
    case 'QUOTE_STALE':
      if (action.workspace !== 'static' || !action.hasFormChanged) {
        return state;
      }
      return removeKey(state, action.tabId);
    case 'QUOTE_RECEIVED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrReplaceKey(state, action.tabId, initialBasketOptimizerState());
    case 'FORM_INPUT_DATA_CHANGED':
      return addOrReplaceKey(state, action.tabId, initialBasketOptimizerState());

    default:
      return state;
  }
}

function onBasketOptimizerUnderlyingTagsChanged(
  state: BasketOptimizerState,
  { tabId, tags }: BasketOptimizerUnderlyingTagsChanged,
): BasketOptimizerState {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      underlyingsTags: tags,
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerOptimizeRequested(
  state: BasketOptimizerState,
  { tabId }: BasketOptimizerOptimizeRequested,
): BasketOptimizerState {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      status: 'loading',
      errorMessage: undefined,
      result: [],
    } satisfies BasketOptimizer,
  };
}

//////////////////////////////////////////////////////
function onQuoteWorkflowQuoteCancel(
  state: BasketOptimizerState,
  { tabId }: BasketOptimizerOptimizeCanceled,
): BasketOptimizerState {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      status: 'idle',
      optimizeId: undefined,
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerOptimizeIdReceived(
  state: BasketOptimizerState,
  { tabId, optimizeId, estimatedComputationTimeInSeconds }: BasketOptimizerOptimizeIdReceived,
): BasketOptimizerState {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      optimizationStartDate: now(),
      estimatedComputationTimeInSeconds,
      optimizeId,
      status: 'loading',
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerResultReceived(state: BasketOptimizerState, { tabId, result }: BasketOptimizerResultReceived) {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      status: 'success',
      optimizationStartDate: undefined,
      estimatedComputationTimeInSeconds: undefined,
      result,
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerOptimizeRequestFailed(
  state: BasketOptimizerState,
  { tabId, errorMessage }: BasketOptimizerOptimizeRequestFailed,
) {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      errorMessage,
      status: 'error',
      optimizeId: undefined,
      optimizationStartDate: undefined,
      estimatedComputationTimeInSeconds: undefined,
      result: [],
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerIsMutuallyExclusiveToggled(
  state: BasketOptimizerState,
  { tabId }: BasketOptimizerIsMutuallyExclusiveToggled,
) {
  const underlyingsTags = state[tabId].underlyingsTags;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      isMutuallyExclusive: !state[tabId].isMutuallyExclusive,
      underlyingsTags: underlyingsTags?.map(tag => ({ ...tag, isHighlighted: false })),
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerCouponFromChanged(
  state: BasketOptimizerState,
  { tabId, value }: BasketOptimizerCouponFromChanged,
) {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      couponFrom: value,
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerTargetBasketSizeChanged(
  state: BasketOptimizerState,
  { tabId, value }: BasketOptimizerTargetBasketSizeChanged,
) {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      targetBasketSize: value,
    } satisfies BasketOptimizer,
  };
}

function basketOptimizerCouponToChanged(state: BasketOptimizerState, { tabId, value }: BasketOptimizerCouponToChanged) {
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      couponTo: value,
    } satisfies BasketOptimizer,
  };
}
